import { Component } from '@theme/component';

/**
 * @typedef {Object} ShareButtonRefs
 * @property {HTMLDetailsElement} details - The details element wrapping the dropdown
 * @property {HTMLElement} summary - The summary trigger element
 * @property {HTMLElement} dropdown - The dropdown panel
 * @property {HTMLButtonElement} copyButton - The copy URL button
 * @property {HTMLSpanElement} copyText - The text span inside the copy button
 */

/**
 * Share button with Web Share API support and copy-to-clipboard fallback.
 * @extends {Component<ShareButtonRefs>}
 */
export class ShareButton extends Component {
  requiredRefs = ['details', 'summary', 'dropdown', 'copyButton', 'copyText'];

  #originalText = '';
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#originalText = this.refs.copyText.textContent;

    const { signal } = this.#abortController;
    document.addEventListener('keydown', this.#handleKeydown, { signal });
    window.addEventListener('scroll', this.#closeDropdown, { signal, passive: true });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    super.disconnectedCallback();
  }

  #closeDropdown = () => {
    if (this.refs.details?.open) this.refs.details.open = false;
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (!this.refs.details?.open) return;

    if (event.key === 'Escape') {
      this.refs.details.open = false;
      this.refs.summary?.focus();
      return;
    }

    if (event.key !== 'Tab') return;
    if (!(document.activeElement instanceof Node) || !this.contains(document.activeElement)) return;

    const focusable = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^="-"])'))
    );
    if (focusable.length < 2) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  handleToggle = () => {
    const { details, summary } = this.refs;

    summary.setAttribute('aria-expanded', details.open.toString());

    if (!details.open) {
      this.classList.remove('share-block--open-up', 'share-block--align-end', 'share-block--positioned');
      return;
    }

    const { dropdown } = this.refs;
    const SPACING = 8;
    const hostRect = this.getBoundingClientRect();
    const dropdownWidth = dropdown.offsetWidth;
    const dropdownHeight = dropdown.offsetHeight;

    const overflowsEnd = hostRect.left + dropdownWidth > window.innerWidth;
    this.classList.toggle('share-block--align-end', overflowsEnd);

    const overflowsBottom = hostRect.bottom + dropdownHeight + SPACING > window.innerHeight;
    const roomAbove = hostRect.top > dropdownHeight + SPACING;
    this.classList.toggle('share-block--open-up', overflowsBottom && roomAbove);

    // Flip classes are now set — reveal the dropdown in its final position.
    this.classList.add('share-block--positioned');
  };

  /** @param {PointerEvent} event */
  handleShare(event) {
    if (!navigator.share || !this.#isTouchDevice()) return;

    event.preventDefault();

    if (this.refs.details?.open) {
      this.refs.details.open = false;
    }

    navigator
      .share({
        title: this.dataset.title,
        url: this.dataset.url,
      })
      .catch(() => {});
  }

  /** @returns {boolean} */
  #isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  async handleCopy() {
    try {
      await navigator.clipboard.writeText(this.dataset.url);

      const copiedText = this.refs.copyButton.dataset.copied;
      if (copiedText) {
        this.refs.copyText.textContent = copiedText;
      }

      setTimeout(() => {
        this.refs.copyText.textContent = this.#originalText;
      }, 2000);
    } catch {
      // Clipboard API not available — fail silently
    }
  }
}

if (!customElements.get('share-button')) {
  customElements.define('share-button', ShareButton);
}
